/**
 * OrderReview.js — Order summary (kind "order-review"), Lightning Rock
 * Winery variant (Commerce7 cart).
 *
 * Exact structural parity with old CHECKOUT.cart.showOrderDetails
 * (RedConcierge/static/javascript/main.js): three groups — "Ship to"/"Pickup by"
 * (own Edit), "Payment" (own Edit, only once a tender is attached — never on
 * first render of this step, matching old parity), "Cart details" (own Edit,
 * opens the cart panel) — then Subtotal/Shipping/Taxes/Estimated total and a
 * footer button whose label and behavior depend on whether a tender is
 * already attached ("Place Order" → charge the existing tender directly,
 * old placeOrderBtn customer_token branch) or not ("Continue to Payment" →
 * old chargeStepForm) + "Continue Shopping".
 *
 * Buyer detail lines are read back from the cart's own shipTo/pickupBy (what
 * the BE echoed after c7ProceedCheckout), not the raw submitted form — same
 * source old showOrderDetails used (cartSummary?.shipTo / .pickupBy).
 *
 * message.data: {
 *   type: "ship"|"pickup",
 *   cart: <Commerce7 cart — items, subTotal, shipping[], taxes[], total,
 *          shipTo|pickupBy, tenders?>,
 *   details: {...normalized fields, for the Edit round-trip to checkout-form}
 * }
 */

import { emit } from "../../../core/state.js";

const EDIT_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="currentColor"/><path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor"/></svg>`;

function cleanDisplay(value) {
  if (value == null) return "";
  const text = String(value).trim();
  return text === "undefined" || text === "null" ? "" : text;
}

function groupHead(titleText, onEdit) {
  const head = document.createElement("div");
  head.className = "ws-order-head";
  const title = document.createElement("p");
  title.className = "ws-order-group-title";
  title.textContent = titleText;
  head.appendChild(title);
  const edit = document.createElement("button");
  edit.type = "button";
  edit.className = "ws-order-edit";
  edit.innerHTML = EDIT_ICON;
  edit.addEventListener("click", onEdit);
  head.appendChild(edit);
  return head;
}

function detailBody() {
  const el = document.createElement("div");
  el.className = "ws-order-details";
  return el;
}

function textLine(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div;
}

export default class OrderReview {
  render(message) {
    const type = message?.data?.type === "pickup" ? "pickup" : "ship";
    const cart = message?.data?.cart || {};
    const details = message?.data?.details || {};
    const items = Array.isArray(cart.items) ? cart.items : [];
    const tenders = Array.isArray(cart.tenders) ? cart.tenders : [];

    const wrap = document.createElement("div");
    wrap.className = "ws-order-review";

    const title = document.createElement("p");
    title.className = "ws-order-title";
    title.textContent = "Order Summary";
    wrap.appendChild(title);

    const divider = document.createElement("hr");
    divider.className = "ws-order-divider";
    wrap.appendChild(divider);

    // ── Group 1: Ship to / Pickup by ──
    const group = document.createElement("div");
    group.className = "ws-order-summary-group";
    group.appendChild(
      groupHead(type === "pickup" ? "Pickup by" : "Ship to", () =>
        // Old showOrderDetails: once a tender is attached, editing Ship (not
        // Pickup) re-opens the saved-address picker instead of a prefilled
        // form — hasTender lets checkout_c7.js branch the same way.
        emit("ws-checkout-edit", { type, values: details, hasTender: tenders.length > 0 }),
      ),
    );

    const body = detailBody();
    const name = [details.firstName, details.lastName]
      .map(cleanDisplay)
      .filter(Boolean)
      .join(" ");
    if (name) body.appendChild(textLine(name));
    if (cleanDisplay(details.company)) {
      body.appendChild(textLine(details.company));
    }
    if (type === "ship") {
      const addr = [details.address, details.address1, details.address2]
        .map(cleanDisplay)
        .filter(Boolean)
        .join(" ");
      if (addr) body.appendChild(textLine(addr));
      const cityLine = [
        cleanDisplay(details.city),
        [details.province, details.zip].map(cleanDisplay).filter(Boolean).join("-"),
      ]
        .filter(Boolean)
        .join(", ");
      if (cityLine) body.appendChild(textLine(cityLine));
    }
    if(cleanDisplay(details.address || details.address1 || details.address2)) {
      body.appendChild(textLine(details?.address));
      body.appendChild(textLine(details?.address1));
      body.appendChild(textLine(details?.address2));
    }
    if (cleanDisplay(details.country)) body.appendChild(textLine(details.country));
    if (type === "ship" && cleanDisplay(details.phone)) {
      body.appendChild(textLine(`Phone: ${details.phone}`));
    }
    // Old: email row only shown for guests (no customer_token).
    if (cleanDisplay(details.email)) {
      body.appendChild(textLine(details.email));
    }
    group.appendChild(body);
    wrap.appendChild(group);

    // ── Group 2: Payment (only once a tender is attached) ──
    if (tenders.length) {
      const payGroup = document.createElement("div");
      payGroup.className = "ws-order-summary-group";
      payGroup.appendChild(
        groupHead("Payment", () =>
          emit("ws-checkout-review-pay", { edit: true }),
        ),
      );
      tenders.forEach(({ creditCard } = {}) => {
        if (!creditCard) return;
        const item = document.createElement("div");
        item.className = "ws-order-tender-item";
        const brand = document.createElement("div");
        brand.style.fontWeight = "600";
        brand.style.textTransform = "uppercase";
        brand.textContent = creditCard.cardBrand || "Card";
        item.appendChild(brand);
        const meta = document.createElement("div");
        meta.style.fontSize = "12px";
        meta.textContent = `${creditCard.maskedCardNumber || ""} | Expiry: ${creditCard.expiryMo}/${creditCard.expiryYr}`;
        item.appendChild(meta);
        payGroup.appendChild(item);
      });
      wrap.appendChild(payGroup);
    }

    // ── Group 3: Cart details ──
    const cartGroup = document.createElement("div");
    cartGroup.className = "ws-order-summary-group";
    cartGroup.appendChild(groupHead("Cart details", () => emit("ws-cart-open")));
    items.forEach((p) => cartGroup.appendChild(this._productCard(p)));
    wrap.appendChild(cartGroup);

    // ── Totals (old showOrderDetails: Subtotal + Shipping[] + Taxes[] + total)
    const totals = document.createElement("div");
    totals.className = "ws-order-totals";

    const hr1 = document.createElement("hr");
    hr1.className = "ws-order-divider";
    totals.appendChild(hr1);

    const table = document.createElement("div");
    table.className = "ws-order-totals-table";
    if (cart.subTotal) table.appendChild(this._totalRow("Subtotal", cart.subTotal));
    // Old createCartProducts cart summary also surfaces bottleDepositTotal.
    if (cart.bottleDepositTotal) {
      table.appendChild(this._totalRow("Bottle Deposit Total", cart.bottleDepositTotal));
    }
    totals.appendChild(table);

    const shippingLines = Array.isArray(cart.shipping) ? cart.shipping : [];
    if (type === "ship") {
      if (shippingLines.length) {
        this._appendChargeSection(totals, "Shipping", shippingLines);
      } else if (cart.shippingTotal) {
        const shipTable = document.createElement("div");
        shipTable.className = "ws-order-totals-table";
        shipTable.appendChild(this._totalRow("Shipping", cart.shippingTotal));
        totals.appendChild(shipTable);
      }
    }

    const taxLines = Array.isArray(cart.taxes) ? cart.taxes : [];
    if (taxLines.length) {
      this._appendChargeSection(totals, "Taxes", taxLines);
    } else if (cart.taxTotal) {
      const taxTable = document.createElement("div");
      taxTable.className = "ws-order-totals-table";
      taxTable.appendChild(this._totalRow("Taxes", cart.taxTotal));
      totals.appendChild(taxTable);
    }

    const hr2 = document.createElement("hr");
    hr2.className = "ws-order-divider";
    totals.appendChild(hr2);

    const est = document.createElement("div");
    est.className = "ws-order-estimated";
    const estLabel = document.createElement("div");
    estLabel.textContent = "Estimated total";
    const estVal = document.createElement("div");
    estVal.textContent = cart.total || "";
    est.appendChild(estLabel);
    est.appendChild(estVal);
    totals.appendChild(est);

    wrap.appendChild(totals);

    // ── Footer: Place Order / Continue to Payment + Continue Shopping ──
    const footer = document.createElement("div");
    footer.className = "ws-order-footer";

    const primary = document.createElement("button");
    primary.type = "button";
    primary.className = "ws-order-checkout-btn";
    if (tenders.length) {
      // Old placeOrderBtn (customer_token branch): a tender is already on the
      // cart — charge it directly instead of re-showing the payment form.
      primary.textContent = "Continue to Payment";
      primary.addEventListener("click", () => {
        primary.disabled = true;
        const cardId = tenders[0]?.creditCard?.customerCreditCardId;
        emit("ws-checkout-charge", { checkoutType: "saved_card", cardId });
      });
    } else {
      primary.textContent = "Continue to Payment";
      primary.addEventListener("click", () => emit("ws-checkout-review-pay"));
    }
    footer.appendChild(primary);

    // Old showCartsProducts: Continue Shopping → sendMessage("Show me all
    // available wines") so the Shop Our Wines cards reload.
    const continueShopping = document.createElement("button");
    continueShopping.type = "button";
    continueShopping.className = "ws-cart-action-btn";
    continueShopping.textContent = "Continue Shopping";
    continueShopping.addEventListener("click", () =>
      emit("ws-prompt-select", {
        label: "Show me all available wines",
        value: "Show me all available wines",
      }),
    );
    footer.appendChild(continueShopping);

    wrap.appendChild(footer);

    return wrap;
  }

  _productCard(p) {
    const line = document.createElement("div");
    line.className = "ws-order-item";

    if (p.image) {
      const imgWrap = document.createElement("div");
      imgWrap.className = "ws-order-item-img-wrap";
      const img = document.createElement("img");
      img.className = "ws-order-item-img";
      img.src = p.image;
      img.alt = p.productTitle || "Item";
      img.loading = "lazy";
      imgWrap.appendChild(img);
      line.appendChild(imgWrap);
    }

    const body = document.createElement("div");
    body.className = "ws-order-item-body";

    const t = document.createElement("strong");
    t.className = "ws-order-item-title";
    t.textContent = p.productTitle || "";
    body.appendChild(t);

    const desc = document.createElement("p");
    desc.className = "ws-order-item-desc";
    desc.textContent = [p.productVariantTitle, `${p.price || ""} per bottle`]
      .filter(Boolean)
      .join(" | ");
    body.appendChild(desc);

    const footer = document.createElement("div");
    footer.className = "ws-order-item-footer";
    const qty = document.createElement("p");
    qty.className = "ws-order-item-qty";
    qty.textContent = `${p.price || ""} X ${p.quantity ?? 1}`;
    const price = document.createElement("p");
    price.className = "ws-order-item-price";
    price.textContent = p.price || "";
    footer.appendChild(qty);
    footer.appendChild(price);
    body.appendChild(footer);

    line.appendChild(body);
    return line;
  }

  _appendChargeSection(parent, heading, lines) {
    const head = document.createElement("div");
    head.className = "ws-order-summary-section-head";
    head.textContent = heading;
    parent.appendChild(head);

    const table = document.createElement("div");
    table.className = "ws-order-totals-table";
    lines.forEach((line) => {
      if (!line || typeof line !== "object") return;
      const label = line.title || line.name || line.label || "";
      const value = line.price ?? line.amount ?? line.total ?? "";
      if (!label && !value) return;
      table.appendChild(this._totalRow(label, value));
    });
    if (table.children.length) parent.appendChild(table);
  }

  _totalRow(label, value) {
    const row = document.createElement("div");
    row.className = "ws-order-total-row";
    const l = document.createElement("span");
    l.textContent = label;
    const v = document.createElement("span");
    v.textContent = value;
    row.appendChild(l);
    row.appendChild(v);
    return row;
  }
}
